import { useState, useEffect, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
} from "@chakra-ui/modal";
import { Button } from "@chakra-ui/react";
import AutoComplete from "react-select";
import axios from "axios";
import { Auth } from "aws-amplify";
import { TokenManager } from "../../../utils/tokenManager";

const activityTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

interface SwitchAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface UserRole {
  merchantId: string;
  subaccountId: string;
  role: string;
  roleId: number;
}

interface SelectOption {
  label: string;
  value: string;
}

const SwitchAccountModal = ({ isOpen, onClose }: SwitchAccountModalProps) => {
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedMerchant, setSelectedMerchant] = useState<SelectOption | null>(null);
  const [selectedSubaccount, setSelectedSubaccount] = useState<SelectOption | null>(null);

  const [roleOptions, setRoleOptions] = useState<string[]>([]);
  const [merchantOptions, setMerchantOptions] = useState<SelectOption[]>([]);
  const [subaccountOptions, setSubaccountOptions] = useState<SelectOption[]>([]);

  const [isFetchingMerchants, setIsFetchingMerchants] = useState(false);
  const [isFetchingSubaccounts, setIsFetchingSubaccounts] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getUserRoles = useCallback((): UserRole[] => {
    try {
      return JSON.parse(localStorage.getItem("userRoles") || "[]");
    } catch {
      return [];
    }
  }, []);

  const getCognitoId = async (): Promise<string> => {
    const session = await Auth.currentSession();
    return session.getIdToken().payload.sub;
  };

  const formatRoleLabel = (role: string): string => {
    return role.replace(/([a-z])([A-Z])/g, "$1 $2");
  };

  // Build role options on modal open
  useEffect(() => {
    if (!isOpen) return;
    const userRoles = getUserRoles();
    const distinctRoles = [...new Set(userRoles.map((r) => r.role))];
    setRoleOptions(distinctRoles);

    // Reset all selections
    setSelectedRole("");
    setSelectedMerchant(null);
    setSelectedSubaccount(null);
    setMerchantOptions([]);
    setSubaccountOptions([]);
    setError(null);
  }, [isOpen, getUserRoles]);

  // Fetch merchants when role changes
  useEffect(() => {
    if (!selectedRole) {
      setMerchantOptions([]);
      setSelectedMerchant(null);
      setSelectedSubaccount(null);
      setSubaccountOptions([]);
      return;
    }

    const userRoles = getUserRoles();
    const roleFilteredMerchantIds = [
      ...new Set(
        userRoles
          .filter((r) => r.role === selectedRole)
          .map((r) => r.merchantId)
      ),
    ];

    const fetchMerchants = async () => {
      setIsFetchingMerchants(true);
      setSelectedMerchant(null);
      setSelectedSubaccount(null);
      setSubaccountOptions([]);
      setError(null);

      try {
        const cognitoId = await getCognitoId();
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/merchant/request`,
          { fn: "get-merchants-to-manage", cognitoId }
        );
        const data = response.data.merchants;
        if (Array.isArray(data)) {
          const filtered = data
            .filter((m: any) => roleFilteredMerchantIds.includes(String(m.id)))
            .map((m: any) => ({ label: m.name, value: String(m.id) }));
          setMerchantOptions(filtered);
        }
      } catch (err) {
        console.error("Error fetching merchants:", err);
        setError("Failed to load merchants");
      }
      setIsFetchingMerchants(false);
    };

    fetchMerchants();
  }, [selectedRole, getUserRoles]);

  // Fetch subaccounts when merchant changes (non-Merchant only)
  useEffect(() => {
    if (!selectedMerchant || !selectedRole || selectedRole.toLowerCase() === "merchant") {
      setSubaccountOptions([]);
      setSelectedSubaccount(null);
      return;
    }

    const userRoles = getUserRoles();
    const roleFilteredSubaccountIds = userRoles
      .filter(
        (r) =>
          r.role === selectedRole &&
          r.merchantId === selectedMerchant.value &&
          r.subaccountId
      )
      .map((r) => r.subaccountId);

    const fetchSubaccounts = async () => {
      setIsFetchingSubaccounts(true);
      setSelectedSubaccount(null);
      setError(null);

      try {
        const cognitoId = await getCognitoId();
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/subaccount/request`,
          {
            fn: "get-subaccount-to-manage",
            cognitoId,
            merchantId: selectedMerchant.value,
          }
        );
        const data = response.data.subaccounts;
        if (Array.isArray(data)) {
          const filtered = data
            .filter((s: any) =>
              roleFilteredSubaccountIds.includes(String(s.id))
            )
            .map((s: any) => ({ label: s.name, value: String(s.id) }));
          setSubaccountOptions(filtered);
        }
      } catch (err) {
        console.error("Error fetching subaccounts:", err);
        setError("Failed to load subaccounts");
      }
      setIsFetchingSubaccounts(false);
    };

    fetchSubaccounts();
  }, [selectedMerchant, selectedRole, getUserRoles]);

  const isMerchantSelected = selectedRole.toLowerCase() === "merchant";
  const canSwitch =
    selectedRole &&
    selectedMerchant &&
    (isMerchantSelected || selectedSubaccount !== null);

  const handleSwitch = async () => {
    if (!canSwitch || !selectedMerchant) return;
    setIsSwitching(true);
    setError(null);

    try {
      const email = localStorage.getItem("userEmail");
      const timeZone = localStorage.getItem("time_zone") || activityTimeZone;

      const body: any = {
        fn: "issue-token-after-selection",
        email,
        merchantId: selectedMerchant.value,
      };

      if (isMerchantSelected) {
        body.isMerchant = true;
      } else if (selectedSubaccount) {
        body.subaccountId = selectedSubaccount.value;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/user/request`,
        body
      );

      const tokenData = response.data;
      if (tokenData.status !== "ok") {
        setError("Failed to switch account. Please try again.");
        setIsSwitching(false);
        return;
      }

      TokenManager.storeTokens(tokenData.token, tokenData.expires, timeZone);

      // Update localStorage
      localStorage.setItem("userRole", selectedRole);
      localStorage.setItem("merchantId", selectedMerchant.value);
      localStorage.setItem("merchantName", selectedMerchant.label);

      if (isMerchantSelected) {
        localStorage.removeItem("subaccountId");
        localStorage.setItem("subaccountName", "");
      } else if (selectedSubaccount) {
        localStorage.setItem("subaccountId", selectedSubaccount.value);
        localStorage.setItem("subaccountName", selectedSubaccount.label);
      }

      // Fetch updated counts
      try {
        const cognitoId = await getCognitoId();
        const merchantResp = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/merchant/request`,
          { fn: "get-merchants-to-manage", cognitoId }
        );
        const merchants = merchantResp.data.merchants;
        localStorage.setItem(
          "merchantsCount",
          Array.isArray(merchants) ? merchants.length.toString() : "1"
        );

        const subResp = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/subaccount/request`,
          {
            fn: "get-subaccount-to-manage",
            cognitoId,
            merchantId: selectedMerchant.value,
          }
        );
        const subs = subResp.data.subaccounts;
        localStorage.setItem(
          "subaccountsCount",
          Array.isArray(subs) ? subs.length.toString() : "0"
        );
      } catch {
        // Best-effort count update
      }

      // Force full page reload to the new role dashboard
      window.location.href = `/${selectedRole.toLowerCase()}/dashboard`;
    } catch (err) {
      console.error("Error switching account:", err);
      setError("An error occurred while switching. Please try again.");
      setIsSwitching(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={!isSwitching}>
      <ModalOverlay className="bg-[#000] !opacity-30" />
      <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
        <ModalBody className="p-6 md:p-7">
          {/* Header */}
          <div className="mb-4 flex items-start gap-3">
            <div className="rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-400 dark:ring-primary-900/30">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
              </svg>
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                Switch Account
              </h1>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                Switch to a different role or account
              </p>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Role dropdown */}
          <div className="mb-5">
            <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              disabled={isSwitching}
              className="custom-select relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-medium text-navy-700 shadow-sm focus:outline-none sm:text-sm"
            >
              <option value="">Select a role</option>
              {roleOptions.map((role) => (
                <option key={role} value={role}>
                  {formatRoleLabel(role)}
                </option>
              ))}
            </select>
          </div>

          {/* Merchant autocomplete */}
          {selectedRole && (
            <div className="mb-5">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                Merchant <span className="text-red-500">*</span>
              </label>
              <AutoComplete
                className="autocomplete-override"
                classNamePrefix="custom-autocomplete"
                isDisabled={isFetchingMerchants || isSwitching}
                placeholder={isFetchingMerchants ? "Retrieving..." : "Please select"}
                options={merchantOptions}
                value={selectedMerchant}
                onChange={(item: any) => setSelectedMerchant(item)}
                isClearable
                noOptionsMessage={() => "No merchants found"}
              />
            </div>
          )}

          {/* Subaccount autocomplete (non-Merchant only) */}
          {selectedRole &&
            selectedMerchant &&
            !isMerchantSelected && (
              <div className="mb-5">
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                  Subaccount <span className="text-red-500">*</span>
                </label>
                <AutoComplete
                  className="autocomplete-override"
                  classNamePrefix="custom-autocomplete"
                  isDisabled={isFetchingSubaccounts || isSwitching}
                  placeholder={isFetchingSubaccounts ? "Retrieving..." : "Please select"}
                  options={subaccountOptions}
                  value={selectedSubaccount}
                  onChange={(item: any) => setSelectedSubaccount(item)}
                  isClearable
                  noOptionsMessage={() => "No subaccounts found"}
                />
              </div>
            )}

          {/* Action buttons */}
          <div className="flex justify-end gap-2">
            <Button
              onClick={onClose}
              isDisabled={isSwitching}
              className="dark:bg-neutral-900 dark:hover:bg-neutral-800 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-white/10 dark:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSwitch}
              isDisabled={!canSwitch || isSwitching}
              isLoading={isSwitching}
              loadingText="Switching..."
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              Switch Account
            </Button>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default SwitchAccountModal;
